import React from 'react';
import { useLocation } from 'react-router-dom';

// Base shimmering block with fluid highlight wave (Light theme optimized)
export function Shimmer({ className = '' }) {
  return (
    <div
      className={`bg-[#e8e6e1] shimmer-wave rounded-lg ${className}`}
    />
  );
}

export function DelayedSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-[#e2e0db] border-t-[#1e3a5f] rounded-full animate-spin" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. ADMIN DASHBOARD SKELETON (1:1 with Dashboard.js)
// ─────────────────────────────────────────────────────────────────────────────
export function DashboardSkeleton() {
  return (
    <div className="lg:grid lg:grid-cols-[1fr_310px] lg:gap-4 lg:items-start space-y-3 sm:space-y-4 lg:space-y-0">
      {/* ── LEFT COLUMN ── */}
      <div className="space-y-3 sm:space-y-4">
        {/* Welcome Hero Banner */}
        <div className="bg-[#1e3a5f] rounded-xl overflow-hidden shadow-sm border border-[#162d4a]">
          <div className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1.5">
                <Shimmer className="h-3 w-28 bg-white/20" />
                <Shimmer className="h-6 w-44 sm:w-52 bg-white/30" />
                <Shimmer className="h-2.5 w-32 bg-white/15" />
              </div>
              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-3 shrink-0">
                <Shimmer className="h-7 w-20 rounded-md bg-white/15 lg:hidden" />
                <div className="text-right space-y-1">
                  <Shimmer className="h-2.5 w-24 bg-white/20 ml-auto" />
                  <Shimmer className="h-7 w-28 bg-emerald-400/30 ml-auto rounded" />
                </div>
              </div>
            </div>
          </div>
          {/* Mini 3-stat strip */}
          <div className="grid grid-cols-3 divide-x divide-white/10 border-t border-white/10 p-2.5 sm:p-3 bg-black/10">
            <div className="px-2 py-1 flex flex-col items-center space-y-1">
              <Shimmer className="h-4 w-12 bg-white/20" />
              <Shimmer className="h-2.5 w-20 bg-white/10" />
            </div>
            <div className="px-2 py-1 flex flex-col items-center space-y-1">
              <Shimmer className="h-4 w-12 bg-white/20" />
              <Shimmer className="h-2.5 w-20 bg-white/10" />
            </div>
            <div className="px-2 py-1 flex flex-col items-center space-y-1">
              <Shimmer className="h-4 w-16 bg-white/20" />
              <Shimmer className="h-2.5 w-20 bg-white/10" />
            </div>
          </div>
        </div>

        {/* 4 Core Stat Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          {[
            'border-l-4 border-l-[#1e3a5f]',
            'border-l-4 border-l-[#d97706]',
            'border-l-4 border-l-[#15803d]',
            'border-l-4 border-l-[#dc2626]'
          ].map((borderClass, idx) => (
            <div
              key={idx}
              className={`p-3 sm:p-3.5 rounded-lg bg-white border border-[#e2e0db] shadow-xs ${borderClass} space-y-2`}
            >
              <div className="flex items-center justify-between">
                <Shimmer className="h-2.5 w-16" />
                <Shimmer className="w-5 h-5 rounded-md" />
              </div>
              <Shimmer className="h-6 w-20" />
              <Shimmer className="h-2 w-14" />
            </div>
          ))}
        </div>

        {/* Today's Delivery Progress Bar Panel */}
        <div className="p-3.5 sm:p-4 rounded-lg bg-white border border-[#e2e0db] shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shimmer className="w-4 h-4 rounded" />
              <Shimmer className="h-3.5 w-36" />
            </div>
            <Shimmer className="h-4 w-16 rounded-full" />
          </div>
          <Shimmer className="h-2.5 w-full rounded-full" />
          {/* 3 Status Pill Filters */}
          <div className="flex gap-2 pt-1">
            <Shimmer className="h-6 w-24 rounded-full" />
            <Shimmer className="h-6 w-24 rounded-full" />
            <Shimmer className="h-6 w-20 rounded-full" />
          </div>
        </div>

        {/* Deliveries List */}
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="p-3 sm:p-3.5 rounded-lg bg-white border border-[#e2e0db] shadow-xs flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Shimmer className="w-9 h-9 rounded-lg shrink-0" />
                <div className="space-y-1.5 flex-1 min-w-0">
                  <Shimmer className="h-3.5 w-36 sm:w-48" />
                  <Shimmer className="h-2.5 w-24 sm:w-32" />
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Shimmer className="h-5 w-16 rounded-full" />
                <Shimmer className="h-6 w-20 rounded-md hidden sm:block" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT COLUMN (Sidebar on Desktop) ── */}
      <div className="space-y-3 sm:space-y-4">
        {/* Quick Actions Panel */}
        <div className="p-3.5 sm:p-4 rounded-lg bg-white border border-[#e2e0db] shadow-xs space-y-3">
          <Shimmer className="h-3 w-28" />
          <div className="grid grid-cols-2 gap-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="p-2.5 rounded-md border border-[#e2e0db] bg-[#faf9f6] flex flex-col items-center text-center space-y-1.5"
              >
                <Shimmer className="w-6 h-6 rounded-md" />
                <Shimmer className="h-2.5 w-14" />
              </div>
            ))}
          </div>
        </div>

        {/* Outstanding Dues Leaderboard */}
        <div className="p-3.5 sm:p-4 rounded-lg bg-white border border-[#e2e0db] shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <Shimmer className="h-3 w-32" />
            <Shimmer className="h-2.5 w-12" />
          </div>
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between p-2 rounded-md bg-[#faf9f6] border border-[#e2e0db]"
              >
                <div className="space-y-1">
                  <Shimmer className="h-3 w-28" />
                  <Shimmer className="h-2 w-16" />
                </div>
                <Shimmer className="h-4 w-16 rounded font-mono" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. ADMIN RETAILERS SKELETON (1:1 with Retailers.js)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailersSkeleton() {
  return (
    <div className="space-y-4 max-w-[calc(100vw-2rem)] lg:max-w-none">
      {/* Enterprise Command Header */}
      <div className="space-y-1.5 pb-2 border-b border-[#e2e0db]">
        <div className="flex items-center justify-between gap-2">
          <Shimmer className="h-6 w-32 rounded" />
          <div className="flex items-center gap-1.5 shrink-0">
            <Shimmer className="h-8 w-24 rounded-md hidden sm:block" />
            <Shimmer className="h-8 w-28 rounded-md" />
          </div>
        </div>
        {/* Inline stats dots */}
        <div className="flex items-center gap-2 pt-0.5">
          <Shimmer className="h-3 w-20" />
          <span className="text-[#c8c5be]">·</span>
          <Shimmer className="h-3 w-16" />
          <span className="text-[#c8c5be]">·</span>
          <Shimmer className="h-3 w-16" />
          <span className="text-[#c8c5be]">·</span>
          <Shimmer className="h-3 w-24" />
        </div>
      </div>

      {/* Area Filter Segmented Control */}
      <div className="flex gap-1.5 overflow-hidden py-1">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Shimmer key={i} className="h-7 w-20 rounded-md shrink-0" />
        ))}
      </div>

      {/* Search & Sort Controls Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
        <Shimmer className="h-9 flex-1 rounded-md" />
        <div className="flex gap-2">
          <Shimmer className="h-9 w-28 rounded-md" />
          <Shimmer className="h-9 w-24 rounded-md" />
        </div>
      </div>

      {/* Desktop Table & Mobile Cards */}
      <div className="bg-white rounded-lg border border-[#e2e0db] shadow-xs overflow-hidden">
        <div className="p-3 border-b border-[#e2e0db] bg-[#faf9f6] flex items-center justify-between">
          <Shimmer className="h-3.5 w-32" />
          <Shimmer className="h-3.5 w-20" />
        </div>
        <div className="divide-y divide-[#e2e0db]">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="p-3 sm:p-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Shimmer className="w-8 h-8 rounded-full shrink-0" />
                <div className="space-y-1.5 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Shimmer className="h-3.5 w-36 sm:w-48" />
                    <Shimmer className="h-3.5 w-16 rounded-full" />
                  </div>
                  <Shimmer className="h-2.5 w-28 sm:w-40" />
                </div>
              </div>
              <div className="text-right space-y-1 shrink-0">
                <Shimmer className="h-4 w-20 ml-auto" />
                <Shimmer className="h-2.5 w-14 ml-auto" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. ADMIN DAILY LEDGER SKELETON (1:1 with DailyLedger.js)
// ─────────────────────────────────────────────────────────────────────────────
export function DailyLedgerSkeleton() {
  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Enterprise Command Header */}
      <div className="bg-white border border-[#e2e0db] rounded-lg shadow-sm overflow-hidden">
        <div className="p-3 sm:p-3.5 border-b border-[#e2e0db] flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#faf9f6]">
          <div className="flex items-center gap-2.5">
            <Shimmer className="h-5 w-40" />
            <Shimmer className="h-5 w-24 rounded font-mono" />
            <span className="text-[#c8c5be] hidden sm:inline">·</span>
            <Shimmer className="h-3 w-36 hidden sm:block" />
          </div>
          <div className="flex items-center gap-2">
            <Shimmer className="h-8 w-28 rounded-md" />
            <Shimmer className="h-8 w-24 rounded-md" />
          </div>
        </div>

        {/* 4 Summary Stat Cells */}
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-[#e2e0db] p-2 bg-white">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-2 sm:p-3 space-y-1">
              <Shimmer className="h-2.5 w-20" />
              <Shimmer className="h-5 w-24" />
            </div>
          ))}
        </div>
      </div>

      {/* Product Summary Row */}
      <div className="flex gap-2 overflow-hidden py-1">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="p-2 rounded-lg bg-white border border-[#e2e0db] shrink-0 w-28 space-y-1">
            <Shimmer className="h-2 w-16" />
            <Shimmer className="h-4 w-12" />
          </div>
        ))}
      </div>

      {/* Daily Dispatch Entry Table */}
      <div className="bg-white rounded-lg border border-[#e2e0db] shadow-xs overflow-hidden">
        <div className="p-3 bg-[#faf9f6] border-b border-[#e2e0db] flex items-center justify-between">
          <Shimmer className="h-3 w-32" />
          <Shimmer className="h-6 w-48 rounded-md" />
        </div>
        <div className="divide-y divide-[#e2e0db]">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Shimmer className="w-6 h-6 rounded" />
                <div className="space-y-1 min-w-0 flex-1">
                  <Shimmer className="h-3.5 w-36" />
                  <Shimmer className="h-2.5 w-24" />
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Shimmer className="h-6 w-16 rounded hidden sm:block" />
                <Shimmer className="h-6 w-16 rounded hidden sm:block" />
                <Shimmer className="h-6 w-20 rounded" />
                <Shimmer className="h-6 w-16 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. ADMIN COMPANY ORDER SKELETON (1:1 with CompanyOrder.js)
// ─────────────────────────────────────────────────────────────────────────────
export function CompanyOrderSkeleton() {
  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Enterprise Top Command Bar */}
      <div className="bg-white border border-[#e2e0db] rounded-lg shadow-sm overflow-hidden">
        <div className="p-3 border-b border-[#e2e0db] flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 bg-[#faf9f6]">
          <div className="flex items-center gap-2">
            <Shimmer className="h-5 w-44" />
            <Shimmer className="h-4 w-20 rounded" />
          </div>
          <div className="flex items-center gap-2">
            <Shimmer className="h-8 w-28 rounded-md" />
            <Shimmer className="h-8 w-32 rounded-md" />
          </div>
        </div>

        {/* Tab & Stat Strip */}
        <div className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex gap-1 bg-[#faf9f6] p-1 rounded-md border border-[#e2e0db]">
            <Shimmer className="h-7 w-28 rounded" />
            <Shimmer className="h-7 w-24 rounded" />
          </div>
          <div className="flex gap-3">
            <Shimmer className="h-4 w-28" />
            <Shimmer className="h-4 w-28" />
          </div>
        </div>
      </div>

      {/* 3 Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-3.5 rounded-lg bg-white border border-[#e2e0db] shadow-xs space-y-1.5">
            <Shimmer className="h-2.5 w-24" />
            <Shimmer className="h-6 w-20" />
            <Shimmer className="h-2 w-16" />
          </div>
        ))}
      </div>

      {/* Company Order Table */}
      <div className="bg-white rounded-lg border border-[#e2e0db] shadow-xs overflow-hidden">
        <div className="p-3 bg-[#faf9f6] border-b border-[#e2e0db] flex items-center justify-between">
          <Shimmer className="h-3.5 w-36" />
          <Shimmer className="h-3.5 w-24" />
        </div>
        <div className="divide-y divide-[#e2e0db]">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="p-3 flex items-center justify-between gap-3">
              <div className="space-y-1 min-w-0 flex-1">
                <Shimmer className="h-3.5 w-44" />
                <Shimmer className="h-2 w-20" />
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Shimmer className="h-6 w-16 rounded font-mono" />
                <Shimmer className="h-8 w-20 rounded-md" />
                <Shimmer className="h-6 w-16 rounded font-mono" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. ADMIN INVENTORY SKELETON (1:1 with Inventory.js)
// ─────────────────────────────────────────────────────────────────────────────
export function InventorySkeleton() {
  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Enterprise Command Bar */}
      <div className="bg-white border border-[#e2e0db] rounded-lg shadow-sm overflow-hidden">
        <div className="p-3 border-b border-[#e2e0db] flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#faf9f6]">
          <div className="space-y-1">
            <Shimmer className="h-5 w-44" />
            <div className="flex items-center gap-2 pt-0.5">
              <Shimmer className="h-3 w-16" />
              <span className="text-[#c8c5be]">·</span>
              <Shimmer className="h-3 w-16" />
              <span className="text-[#c8c5be]">·</span>
              <Shimmer className="h-3 w-20" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Shimmer className="h-8 w-24 rounded-md" />
            <Shimmer className="h-8 w-20 rounded-md" />
            <Shimmer className="h-8 w-28 rounded-md" />
          </div>
        </div>

        {/* Search, Type Tabs & Category Row */}
        <div className="p-3 space-y-2.5">
          <div className="flex flex-col sm:flex-row gap-2">
            <Shimmer className="h-9 flex-1 rounded-md" />
            <div className="flex gap-1">
              <Shimmer className="h-9 w-20 rounded-md" />
              <Shimmer className="h-9 w-20 rounded-md" />
              <Shimmer className="h-9 w-20 rounded-md" />
            </div>
          </div>
          <div className="flex gap-1.5 overflow-hidden">
            {[1, 2, 3, 4, 5].map((i) => (
              <Shimmer key={i} className="h-6 w-20 rounded-full shrink-0" />
            ))}
          </div>
        </div>
      </div>

      {/* Product List Table */}
      <div className="bg-white rounded-lg border border-[#e2e0db] shadow-xs overflow-hidden">
        <div className="divide-y divide-[#e2e0db]">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="p-3 sm:p-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Shimmer className="w-10 h-10 rounded-lg shrink-0" />
                <div className="space-y-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Shimmer className="h-3.5 w-40" />
                    <Shimmer className="h-3.5 w-16 rounded-full" />
                  </div>
                  <Shimmer className="h-2.5 w-24" />
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right space-y-1">
                  <Shimmer className="h-4 w-16 ml-auto font-mono" />
                  <Shimmer className="h-2 w-10 ml-auto" />
                </div>
                <Shimmer className="h-8 w-8 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. ADMIN LEDGER SKELETON (1:1 with Ledger.js)
// ─────────────────────────────────────────────────────────────────────────────
export function LedgerSkeleton() {
  return (
    <div className="space-y-3 sm:space-y-4 pb-20 sm:pb-12">
      {/* Enterprise Header Card */}
      <div className="bg-white p-3 sm:p-3.5 rounded-lg border border-[#e2e0db] shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
          <div className="space-y-1">
            <Shimmer className="h-5 w-48" />
            <Shimmer className="h-2.5 w-36" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Shimmer className="h-8 w-24 rounded-md" />
            <Shimmer className="h-8 w-24 rounded-md" />
            <Shimmer className="h-8 w-28 rounded-md" />
          </div>
        </div>

        {/* Tabs: Statement vs Dues */}
        <div className="flex gap-2 border-t border-[#e2e0db] pt-3">
          <Shimmer className="h-8 w-36 rounded-md" />
          <Shimmer className="h-8 w-36 rounded-md" />
        </div>
      </div>

      {/* Dues & Statement Overview Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-3 rounded-lg bg-white border border-[#e2e0db] shadow-xs space-y-1">
            <Shimmer className="h-2.5 w-20" />
            <Shimmer className="h-5 w-24 font-mono" />
          </div>
        ))}
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-lg border border-[#e2e0db] shadow-xs overflow-hidden">
        <div className="p-3 bg-[#faf9f6] border-b border-[#e2e0db] flex items-center justify-between">
          <Shimmer className="h-3.5 w-32" />
          <Shimmer className="h-7 w-40 rounded-md" />
        </div>
        <div className="divide-y divide-[#e2e0db]">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="p-3 flex items-center justify-between gap-3">
              <div className="space-y-1 min-w-0 flex-1">
                <Shimmer className="h-3.5 w-36" />
                <Shimmer className="h-2 w-24" />
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Shimmer className="h-4 w-16 font-mono" />
                <Shimmer className="h-4 w-16 font-mono" />
                <Shimmer className="h-5 w-20 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. ADMIN SUPPORT TICKETS SKELETON (1:1 with SupportTickets.js)
// ─────────────────────────────────────────────────────────────────────────────
export function SupportTicketsSkeleton() {
  return (
    <div className="space-y-3 sm:space-y-4 pb-20 sm:pb-12">
      {/* Header Card */}
      <div className="bg-white p-3 sm:p-3.5 rounded-lg border border-[#e2e0db] shadow-sm flex items-start justify-between gap-2">
        <div className="space-y-1 flex-1">
          <Shimmer className="h-5 w-36" />
          <div className="flex items-center gap-2 pt-0.5">
            <Shimmer className="h-3 w-20" />
            <span className="text-[#c8c5be]">·</span>
            <Shimmer className="h-3 w-16" />
            <span className="text-[#c8c5be]">·</span>
            <Shimmer className="h-3 w-20" />
          </div>
        </div>
        <Shimmer className="w-8 h-8 rounded-md shrink-0" />
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1.5 overflow-hidden">
        {[1, 2, 3, 4].map((i) => (
          <Shimmer key={i} className="h-8 w-24 rounded-md shrink-0" />
        ))}
      </div>

      {/* Tickets Feed */}
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-4 rounded-xl bg-white border border-[#e2e0db] shadow-xs space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1 flex-1">
                <div className="flex items-center gap-2">
                  <Shimmer className="h-4 w-36" />
                  <Shimmer className="h-4 w-16 rounded-full" />
                </div>
                <Shimmer className="h-2.5 w-28" />
              </div>
              <Shimmer className="h-7 w-28 rounded-md" />
            </div>
            <Shimmer className="h-10 w-full rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. ADMIN PLACE ORDER SKELETON (1:1 with PlaceOrder.js - Admin)
// ─────────────────────────────────────────────────────────────────────────────
export function AdminPlaceOrderSkeleton() {
  return (
    <div className="w-full pb-28 lg:pb-6 space-y-3 sm:space-y-4">
      {/* Top Bar on Mobile */}
      <div className="lg:hidden p-3 rounded-lg bg-white border border-[#e2e0db] space-y-2.5 shadow-sm">
        <Shimmer className="h-10 w-full rounded-md" />
        <div className="flex justify-between items-center pt-1 border-t border-[#e2e0db]">
          <Shimmer className="h-3 w-24" />
          <div className="flex gap-1.5">
            <Shimmer className="h-7 w-20 rounded-md" />
            <Shimmer className="h-7 w-24 rounded-md" />
          </div>
        </div>
      </div>

      {/* Desktop 2-Column Layout */}
      <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-start">
        {/* Left Column: Retailer Picker & Search (7 Cols) */}
        <div className="lg:col-span-7 bg-white p-4 rounded-xl border border-[#e2e0db] shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <Shimmer className="h-4 w-36" />
            <Shimmer className="h-7 w-48 rounded-md" />
          </div>
          <div className="flex gap-1.5 overflow-hidden">
            {[1, 2, 3, 4].map((i) => (
              <Shimmer key={i} className="h-6 w-16 rounded-full shrink-0" />
            ))}
          </div>
          <div className="divide-y divide-[#e2e0db] max-h-[480px] overflow-hidden">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="py-2.5 flex items-center justify-between">
                <div className="space-y-1">
                  <Shimmer className="h-3.5 w-40" />
                  <Shimmer className="h-2.5 w-24" />
                </div>
                <Shimmer className="h-7 w-16 rounded-md" />
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Order Entry & Products (5 Cols) */}
        <div className="lg:col-span-5 bg-white p-4 rounded-xl border border-[#e2e0db] shadow-xs space-y-3.5">
          <div className="flex items-center justify-between pb-2 border-b border-[#e2e0db]">
            <Shimmer className="h-4 w-28" />
            <Shimmer className="h-6 w-24 rounded-md" />
          </div>
          <div className="space-y-2.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-2.5 rounded-lg bg-[#faf9f6] border border-[#e2e0db] flex items-center justify-between">
                <div className="space-y-1">
                  <Shimmer className="h-3 w-32" />
                  <Shimmer className="h-2 w-16" />
                </div>
                <Shimmer className="h-8 w-24 rounded-lg" />
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-[#e2e0db] space-y-2">
            <div className="flex justify-between">
              <Shimmer className="h-3.5 w-20" />
              <Shimmer className="h-5 w-24 font-mono" />
            </div>
            <Shimmer className="h-10 w-full rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 9. ADMIN ANNOUNCEMENTS SKELETON (1:1 with Announcements.js)
// ─────────────────────────────────────────────────────────────────────────────
export function AdminAnnouncementsSkeleton() {
  return (
    <div className="space-y-4 pb-20 sm:pb-12 max-w-[calc(100vw-2rem)] lg:max-w-none">
      {/* Enterprise Header */}
      <div className="space-y-1.5 pb-2 border-b border-[#e2e0db]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Shimmer className="w-5 h-5 rounded" />
            <Shimmer className="h-6 w-44 rounded" />
          </div>
          <Shimmer className="h-7 w-28 rounded-full" />
        </div>
        <Shimmer className="h-3 w-64 pt-0.5" />
      </div>

      {/* Tab Switcher on Mobile */}
      <div className="lg:hidden flex gap-1 bg-white p-1 rounded-xl border border-[#e2e0db]">
        <Shimmer className="h-8 flex-1 rounded-lg" />
        <Shimmer className="h-8 flex-1 rounded-lg" />
      </div>

      {/* 2-Column Grid on Desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* Left: Composer Form (5 Cols) */}
        <div className="lg:col-span-5 bg-white p-4 rounded-xl border border-[#e2e0db] shadow-xs space-y-3.5">
          <Shimmer className="h-4 w-36" />
          <div className="space-y-2">
            <Shimmer className="h-3 w-20" />
            <Shimmer className="h-9 w-full rounded-lg" />
          </div>
          <div className="space-y-2">
            <Shimmer className="h-3 w-24" />
            <Shimmer className="h-20 w-full rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Shimmer className="h-9 rounded-lg" />
            <Shimmer className="h-9 rounded-lg" />
          </div>
          <Shimmer className="h-10 w-full rounded-xl" />
        </div>

        {/* Right: Broadcast Logs & Active Feed (7 Cols) */}
        <div className="lg:col-span-7 bg-white p-4 rounded-xl border border-[#e2e0db] shadow-xs space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-[#e2e0db]">
            <Shimmer className="h-4 w-40" />
            <Shimmer className="h-3 w-16" />
          </div>
          <div className="space-y-2.5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="p-3 rounded-xl border border-[#e2e0db] bg-[#faf9f6] space-y-2">
                <div className="flex items-center justify-between">
                  <Shimmer className="h-4 w-28 rounded-full" />
                  <Shimmer className="h-2.5 w-20" />
                </div>
                <Shimmer className="h-3.5 w-full" />
                <Shimmer className="h-3.5 w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 10. ADMIN SETTINGS SKELETON (1:1 with Settings.js)
// ─────────────────────────────────────────────────────────────────────────────
export function AdminSettingsSkeleton() {
  return (
    <div className="w-full max-w-6xl mx-auto space-y-3.5 pb-24 lg:pb-8 min-w-0">
      {/* Header Strip */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white p-3.5 sm:p-4 rounded-xl border border-[#e2e0db] shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Shimmer className="w-4 h-4 rounded" />
            <Shimmer className="h-5 w-44 rounded" />
          </div>
          <Shimmer className="h-2.5 w-60" />
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Shimmer className="h-7 w-24 rounded-full" />
          <Shimmer className="h-9 w-32 rounded-xl" />
        </div>
      </div>

      {/* Tabs Navigation Strip */}
      <div className="flex gap-1.5 overflow-hidden py-1">
        {[1, 2, 3, 4].map((i) => (
          <Shimmer key={i} className="h-9 w-36 rounded-xl shrink-0" />
        ))}
      </div>

      {/* Form Card Panel */}
      <div className="bg-white p-4 sm:p-5 rounded-2xl border border-[#e2e0db] shadow-xs space-y-4">
        <div className="pb-3 border-b border-[#e2e0db] space-y-1">
          <Shimmer className="h-4 w-40" />
          <Shimmer className="h-2.5 w-72" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-1.5 p-3 rounded-xl bg-[#faf9f6] border border-[#e2e0db]">
              <Shimmer className="h-3 w-28" />
              <Shimmer className="h-9 w-full rounded-lg" />
              <Shimmer className="h-2 w-48" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 11. ADMIN SESSIONS SKELETON (1:1 with Sessions.js)
// ─────────────────────────────────────────────────────────────────────────────
export function SessionsSkeleton() {
  return (
    <div className="space-y-4 max-w-[calc(100vw-2rem)] lg:max-w-none">
      {/* Enterprise Command Header */}
      <div className="space-y-1.5 pb-2 border-b border-[#e2e0db]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Shimmer className="h-6 w-36 rounded" />
            <Shimmer className="h-5 w-20 rounded-full" />
          </div>
          <Shimmer className="h-8 w-28 rounded-md" />
        </div>
        <Shimmer className="h-3 w-56" />
      </div>

      {/* Filter Tabs & Search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
        <div className="flex gap-1.5">
          <Shimmer className="h-8 w-20 rounded-lg" />
          <Shimmer className="h-8 w-20 rounded-lg" />
          <Shimmer className="h-8 w-20 rounded-lg" />
        </div>
        <Shimmer className="h-8 w-48 rounded-lg" />
      </div>

      {/* Sessions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="p-3.5 rounded-xl bg-white border border-[#e2e0db] shadow-xs space-y-2.5">
            <div className="flex items-center justify-between">
              <Shimmer className="h-3.5 w-32" />
              <Shimmer className="h-4 w-16 rounded-full" />
            </div>
            <div className="space-y-1">
              <Shimmer className="h-2.5 w-40" />
              <Shimmer className="h-2 w-28" />
            </div>
            <div className="pt-2 border-t border-[#e2e0db] flex justify-between items-center">
              <Shimmer className="h-2 w-20" />
              <Shimmer className="h-6 w-16 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 12. ADMIN ORDER DETAIL SKELETON (1:1 with OrderDetail.js)
// ─────────────────────────────────────────────────────────────────────────────
export function OrderDetailSkeleton() {
  return (
    <div className="space-y-3 sm:space-y-4 pb-36 sm:pb-12 w-full max-w-full overflow-hidden">
      {/* Top Command Header */}
      <div className="bg-white border border-[#e2e0db] rounded-lg p-3 sm:p-3.5 shadow-sm flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Shimmer className="w-8 h-8 rounded-lg" />
          <div className="space-y-1">
            <Shimmer className="h-4 w-36" />
            <Shimmer className="h-2.5 w-48" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Shimmer className="h-8 w-20 rounded-md" />
          <Shimmer className="h-8 w-24 rounded-md" />
        </div>
      </div>

      {/* Order Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-3 rounded-lg bg-white border border-[#e2e0db] space-y-1.5 shadow-xs">
            <Shimmer className="h-2.5 w-20" />
            <Shimmer className="h-4 w-32" />
            <Shimmer className="h-2 w-24" />
          </div>
        ))}
      </div>

      {/* Items Manifest Table */}
      <div className="bg-white rounded-lg border border-[#e2e0db] shadow-xs overflow-hidden">
        <div className="p-3 bg-[#faf9f6] border-b border-[#e2e0db] flex items-center justify-between">
          <Shimmer className="h-3.5 w-28" />
          <Shimmer className="h-3.5 w-20" />
        </div>
        <div className="divide-y divide-[#e2e0db]">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-3 flex items-center justify-between">
              <div className="space-y-1">
                <Shimmer className="h-3.5 w-36" />
                <Shimmer className="h-2.5 w-20" />
              </div>
              <div className="flex items-center gap-3">
                <Shimmer className="h-4 w-12 font-mono" />
                <Shimmer className="h-4 w-20 font-mono" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 13. RETAILER HOME SKELETON (1:1 with Home.js - Current Code Layout)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailerHomeSkeleton() {
  return (
    <div className="w-full space-y-4 pb-24 lg:pb-8">
      {/* 1. Store Identity Hero Banner */}
      <div className="w-full bg-[#1e3a5f] rounded-2xl p-4 sm:p-5 lg:p-6 shadow-sm relative overflow-hidden">
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Shimmer className="h-3 w-20 bg-white/20" />
              <span className="text-white/40 text-xs">•</span>
              <Shimmer className="h-3 w-28 bg-emerald-400/30" />
            </div>
            <Shimmer className="h-8 sm:h-9 w-48 sm:w-64 bg-white/30 rounded-lg" />
            <Shimmer className="h-3.5 w-40 bg-white/20 rounded" />
          </div>
          <Shimmer className="h-10 w-36 bg-white/90 rounded-xl shrink-0" />
        </div>
        {/* Cut-off & Delivery Timing Strip */}
        <div className="relative z-10 mt-3 pt-3 border-t border-white/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <Shimmer className="h-3 w-48 bg-white/20" />
          <Shimmer className="h-3 w-44 bg-white/20" />
        </div>
      </div>

      {/* 2. Outstanding Dues Card */}
      <div className="w-full bg-white border-2 border-[#e2e0db] rounded-2xl p-4 sm:p-5 shadow-sm space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <Shimmer className="h-3 w-44 sm:w-56" />
          </div>
          <Shimmer className="h-7 w-28 rounded-xl" />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 pt-1 border-t border-[#f0eee9]">
          <div className="space-y-1.5">
            <Shimmer className="h-9 sm:h-10 w-36 sm:w-48 font-mono rounded" />
            <Shimmer className="h-5 w-44 rounded-md" />
          </div>
          <Shimmer className="h-3 w-48" />
        </div>
      </div>

      {/* 3. Today's Order / Booking Open Card */}
      <div className="w-full bg-white border border-[#e2e0db] rounded-2xl p-4 sm:p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="space-y-1.5 flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <Shimmer className="h-3 w-32" />
          </div>
          <Shimmer className="h-5 w-60 sm:w-72" />
          <Shimmer className="h-3 w-80 max-w-full" />
        </div>
        <Shimmer className="h-10 w-44 rounded-xl shrink-0" />
      </div>

      {/* 4. Quick Touch Workbench Tiles (4 Grid) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white border border-[#e2e0db] rounded-2xl p-3.5 sm:p-4 shadow-xs flex flex-col justify-between space-y-3 h-28"
          >
            <div className="flex items-center justify-between">
              <Shimmer className="w-8 h-8 rounded-xl bg-[#1e3a5f]/15" />
              <Shimmer className="h-2.5 w-12" />
            </div>
            <div className="space-y-1">
              <Shimmer className="h-3.5 w-20" />
              <Shimmer className="h-2.5 w-16" />
            </div>
          </div>
        ))}
      </div>

      {/* 5. Full-Width 2-Column Archive & Support Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start w-full">
        {/* Recent Orders Feed (8 Cols) */}
        <div className="lg:col-span-8 bg-white border border-[#e2e0db] rounded-2xl p-4 sm:p-5 shadow-xs space-y-3">
          <div className="flex items-center justify-between pb-2.5 border-b border-[#e2e0db]">
            <Shimmer className="h-3.5 w-36" />
            <Shimmer className="h-3 w-16" />
          </div>
          <div className="divide-y divide-[#f0eee9]">
            {[1, 2, 3].map((i) => (
              <div key={i} className="py-2.5 flex items-center justify-between gap-3">
                <div className="space-y-1 flex-1 min-w-0">
                  <Shimmer className="h-3.5 w-32" />
                  <Shimmer className="h-2.5 w-48" />
                </div>
                <div className="text-right space-y-1 shrink-0">
                  <Shimmer className="h-4 w-16 ml-auto font-mono" />
                  <Shimmer className="h-4 w-14 rounded-full ml-auto" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Support & Operations Info (4 Cols) */}
        <div className="lg:col-span-4 space-y-3">
          <div className="bg-white border border-[#e2e0db] rounded-2xl p-4 sm:p-5 shadow-xs space-y-3">
            <Shimmer className="h-3.5 w-36" />
            <div className="space-y-2">
              <div className="flex justify-between">
                <Shimmer className="h-3 w-28" />
                <Shimmer className="h-3 w-20 font-mono" />
              </div>
              <div className="flex justify-between">
                <Shimmer className="h-3 w-28" />
                <Shimmer className="h-3 w-24 font-mono" />
              </div>
            </div>
          </div>

          <div className="bg-[#faf9f6] border border-[#e2e0db] rounded-2xl p-4 sm:p-5 space-y-2.5">
            <Shimmer className="h-3 w-28" />
            <Shimmer className="h-3 w-full" />
            <div className="pt-1 flex gap-2">
              <Shimmer className="h-9 flex-1 rounded-xl" />
              <Shimmer className="h-9 flex-1 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 14. RETAILER PLACE ORDER SKELETON (1:1 with PlaceOrder.js - Current Code Layout)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailerPlaceOrderSkeleton() {
  return (
    <div className="w-full space-y-3 sm:space-y-4 pb-36 lg:pb-8">
      {/* 1. Order Cut-Off & Due Briefing Banner */}
      <div className="w-full bg-[#1e3a5f] rounded-2xl p-3 sm:p-5 shadow-sm space-y-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping shrink-0" />
            <div className="space-y-1">
              <Shimmer className="h-3.5 sm:h-4 w-48 sm:w-64 bg-white/30" />
              <Shimmer className="h-2.5 w-72 bg-white/15 hidden sm:block" />
            </div>
          </div>
          <Shimmer className="h-3 w-40 bg-white/20 hidden sm:block" />
        </div>
        {/* Due Alert Pill */}
        <div className="p-2 sm:p-2.5 rounded-xl bg-white/10 border border-white/15 flex items-center justify-between">
          <Shimmer className="h-3 w-40 bg-white/25" />
          <Shimmer className="h-3 w-24 bg-white/25" />
        </div>
      </div>

      {/* 2. Search, Categories & View Switcher Toolbar */}
      <div className="bg-white p-2.5 sm:p-3.5 rounded-2xl border border-[#e2e0db] shadow-xs space-y-2 sm:space-y-2.5 w-full">
        <div className="flex items-center justify-between gap-2 w-full">
          <Shimmer className="h-9 flex-1 rounded-xl" />
          <div className="flex gap-1 shrink-0">
            <Shimmer className="h-9 w-20 rounded-xl" />
            <Shimmer className="h-9 w-24 rounded-xl" />
          </div>
        </div>
        {/* Category Pills Strip */}
        <div className="pt-2 border-t border-[#f0eee9] flex gap-1.5 overflow-hidden">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Shimmer key={i} className="h-7 w-20 rounded-lg shrink-0" />
          ))}
        </div>
      </div>

      {/* 3. Workstation 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start w-full">
        {/* Left Column: Product Catalog (8 Cols) */}
        <div className="lg:col-span-8 space-y-3 min-w-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="bg-white border border-[#e2e0db] rounded-2xl p-3 shadow-xs space-y-2.5 flex flex-col justify-between"
              >
                <Shimmer className="w-full h-24 rounded-xl" />
                <div className="space-y-1">
                  <Shimmer className="h-2 w-14 rounded" />
                  <Shimmer className="h-3.5 w-full rounded" />
                  <div className="flex items-center justify-between pt-1">
                    <Shimmer className="h-4 w-16 font-mono" />
                    <Shimmer className="h-2 w-10" />
                  </div>
                </div>
                {/* Stepper Buttons [- 0 +] */}
                <div className="flex items-center justify-between gap-1 pt-1 border-t border-[#f0eee9]">
                  <Shimmer className="h-7 w-8 rounded-lg" />
                  <Shimmer className="h-6 w-10 rounded font-mono" />
                  <Shimmer className="h-7 w-8 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Sticky Cart Order Summary (4 Cols - Desktop) */}
        <div className="hidden lg:block lg:col-span-4 bg-white border border-[#e2e0db] rounded-2xl p-4 sm:p-5 shadow-xs space-y-3.5 sticky top-20">
          <div className="flex items-center justify-between pb-2.5 border-b border-[#e2e0db]">
            <Shimmer className="h-4 w-28" />
            <Shimmer className="h-4 w-12 rounded-full" />
          </div>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex justify-between items-center py-1">
                <Shimmer className="h-3 w-32" />
                <Shimmer className="h-3 w-16 font-mono" />
              </div>
            ))}
          </div>
          <div className="pt-2.5 border-t border-[#e2e0db] space-y-2">
            <div className="flex justify-between items-center">
              <Shimmer className="h-3.5 w-20" />
              <Shimmer className="h-5 w-24 font-mono" />
            </div>
            <Shimmer className="h-10 w-full rounded-xl" />
          </div>
        </div>
      </div>

      {/* Floating Bottom Cart Bar for Mobile */}
      <div className="fixed bottom-[5rem] lg:hidden left-3 right-3 z-40">
        <div className="w-full bg-[#1e3a5f] rounded-2xl shadow-xl p-3 flex items-center justify-between border border-[#162d4a]">
          <div className="space-y-1">
            <Shimmer className="h-3.5 w-24 bg-white/30" />
            <Shimmer className="h-2 w-16 bg-white/20" />
          </div>
          <Shimmer className="h-9 w-32 rounded-xl bg-emerald-400/80" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 15. RETAILER CHECKOUT SKELETON (1:1 with Checkout.js)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailerCheckoutSkeleton() {
  return (
    <div className="w-full space-y-3 sm:space-y-4 pb-36 lg:pb-12 max-w-3xl mx-auto">
      {/* Back button & Header */}
      <div className="flex items-center justify-between gap-3 pt-1 pb-1">
        <div className="flex items-center gap-2">
          <Shimmer className="w-8 h-8 rounded-xl" />
          <div className="space-y-1">
            <Shimmer className="h-5 w-44 rounded-md" />
            <Shimmer className="h-2.5 w-32 rounded" />
          </div>
        </div>
        <Shimmer className="h-6 w-20 rounded-full" />
      </div>

      {/* Dispatch summary banner */}
      <div className="w-full bg-[#1e3a5f] rounded-2xl p-4 sm:p-5 shadow-sm space-y-2">
        <div className="flex items-center justify-between">
          <Shimmer className="h-3.5 w-36 bg-white/30" />
          <Shimmer className="h-3 w-28 bg-white/20 font-mono" />
        </div>
        <Shimmer className="h-3 w-64 bg-white/20" />
      </div>

      {/* Itemized Order Review Card */}
      <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 sm:p-5 shadow-xs space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-[#f0eee9]">
          <Shimmer className="h-3.5 w-36" />
          <Shimmer className="h-3 w-16" />
        </div>
        <div className="divide-y divide-[#f0eee9]">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="py-2.5 flex items-center justify-between gap-3">
              <div className="space-y-1 flex-1">
                <Shimmer className="h-3.5 w-36" />
                <Shimmer className="h-2.5 w-20" />
              </div>
              <div className="text-right space-y-1 shrink-0">
                <Shimmer className="h-4 w-16 ml-auto font-mono" />
                <Shimmer className="h-2.5 w-10 ml-auto" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ledger Due Card */}
      <div className="p-3.5 sm:p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-between">
        <div className="space-y-1">
          <Shimmer className="h-3 w-36 bg-amber-200" />
          <Shimmer className="h-2 w-48 bg-amber-200/60" />
        </div>
        <Shimmer className="h-5 w-24 bg-amber-300 rounded font-mono" />
      </div>

      {/* Total & Confirm Dock */}
      <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 shadow-xs space-y-3">
        <div className="flex justify-between items-center">
          <Shimmer className="h-4 w-24" />
          <Shimmer className="h-6 w-28 font-mono" />
        </div>
        <Shimmer className="h-11 w-full rounded-xl bg-emerald-600/30" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 16. RETAILER TRACK ORDER SKELETON (1:1 with TrackOrder.js)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailerTrackOrderSkeleton() {
  return (
    <div className="w-full space-y-3.5 sm:space-y-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-2.5 pt-0.5 pb-1">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Shimmer className="h-5 w-32" />
            <Shimmer className="h-5 w-20 rounded-full" />
          </div>
          <Shimmer className="h-2.5 w-44" />
        </div>
        <Shimmer className="w-8 h-8 rounded-xl" />
      </div>

      {/* 2-Column Desktop Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start w-full">
        {/* Left (8 Cols): Timeline & Manifest */}
        <div className="lg:col-span-8 space-y-4 min-w-0">
          {/* Visual 4-Step Timeline Card */}
          <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 sm:p-5 shadow-xs space-y-4">
            <div className="flex justify-between items-center">
              <Shimmer className="h-3.5 w-36" />
              <Shimmer className="h-3 w-28 font-mono" />
            </div>
            {/* 4 Steps */}
            <div className="flex items-center justify-between max-w-xl mx-auto py-2">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex flex-col items-center gap-1.5 shrink-0">
                  <Shimmer className="w-8 h-8 rounded-full" />
                  <Shimmer className="h-2 w-14" />
                </div>
              ))}
            </div>
          </div>

          {/* Delivery Driver / Depot Contact Card */}
          <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 shadow-xs flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shimmer className="w-10 h-10 rounded-xl" />
              <div className="space-y-1">
                <Shimmer className="h-3.5 w-36" />
                <Shimmer className="h-2.5 w-28" />
              </div>
            </div>
            <Shimmer className="h-8 w-24 rounded-xl" />
          </div>

          {/* Manifest Table Card */}
          <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 sm:p-5 shadow-xs space-y-3">
            <Shimmer className="h-3.5 w-36" />
            <div className="divide-y divide-[#f0eee9]">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="py-2.5 flex items-center justify-between">
                  <div className="space-y-1">
                    <Shimmer className="h-3.5 w-40" />
                    <Shimmer className="h-2.5 w-20" />
                  </div>
                  <Shimmer className="h-4 w-16 font-mono" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right (4 Cols): Summary & Actions */}
        <div className="lg:col-span-4 space-y-3">
          <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 sm:p-5 shadow-xs space-y-3">
            <Shimmer className="h-3.5 w-32" />
            <div className="space-y-2 pt-1 border-t border-[#f0eee9]">
              <div className="flex justify-between">
                <Shimmer className="h-3 w-24" />
                <Shimmer className="h-3 w-16 font-mono" />
              </div>
              <div className="flex justify-between">
                <Shimmer className="h-3 w-28" />
                <Shimmer className="h-3 w-16 font-mono" />
              </div>
              <div className="flex justify-between pt-2 border-t border-[#f0eee9]">
                <Shimmer className="h-4 w-24" />
                <Shimmer className="h-5 w-24 font-mono" />
              </div>
            </div>
            <Shimmer className="h-10 w-full rounded-xl bg-emerald-600/30" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 17. RETAILER ORDER HISTORY SKELETON (1:1 with OrderHistory.js)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailerOrderHistorySkeleton() {
  return (
    <div className="w-full space-y-4 pb-24 lg:pb-12">
      {/* Header & Month Switcher */}
      <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 sm:p-5 shadow-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3.5">
        <div className="space-y-1.5">
          <Shimmer className="h-6 w-48 rounded-lg" />
          <Shimmer className="h-3.5 w-64 rounded-md" />
        </div>
        <Shimmer className="h-9 w-40 rounded-xl" />
      </div>

      {/* 4 KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-2xl border border-[#e2e0db] p-3.5 sm:p-4 shadow-xs space-y-2">
            <div className="flex justify-between items-center">
              <Shimmer className="h-3 w-20" />
              <Shimmer className="w-7 h-7 rounded-lg" />
            </div>
            <Shimmer className="h-7 w-28 rounded-lg font-mono" />
            <Shimmer className="h-2.5 w-24" />
          </div>
        ))}
      </div>

      {/* Filter Row */}
      <div className="flex justify-between items-center gap-2">
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((i) => (
            <Shimmer key={i} className="h-8 w-20 rounded-xl" />
          ))}
        </div>
        <Shimmer className="h-8 w-48 rounded-xl hidden sm:block" />
      </div>

      {/* Orders List Feed */}
      <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 shadow-xs space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="py-3 flex items-center justify-between border-b border-[#f0eee9] last:border-0">
            <div className="flex items-center gap-3">
              <Shimmer className="w-8 h-8 rounded-xl bg-[#1e3a5f]/15" />
              <div className="space-y-1">
                <Shimmer className="h-4 w-32" />
                <Shimmer className="h-3 w-40" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shimmer className="h-5 w-20 rounded-md font-mono" />
              <Shimmer className="h-7 w-24 rounded-lg hidden sm:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 18. RETAILER MY LEDGER SKELETON (1:1 with MyLedger.js)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailerMyLedgerSkeleton() {
  return (
    <div className="space-y-3 sm:space-y-4 pb-20 sm:pb-12">
      {/* Mobile Unified Card */}
      <div className="sm:hidden bg-white p-3 rounded-xl border border-[#e2e0db] shadow-2xs space-y-2.5">
        <div className="flex items-center justify-between pb-2 border-b border-[#f0eee9]">
          <div className="space-y-1">
            <Shimmer className="h-5 w-36" />
            <Shimmer className="h-3 w-28" />
          </div>
          <Shimmer className="w-8 h-8 rounded-lg" />
        </div>
        <div className="flex justify-between items-baseline">
          <div className="space-y-1">
            <Shimmer className="h-2.5 w-24" />
            <Shimmer className="h-7 w-32 font-mono" />
          </div>
          <Shimmer className="h-4 w-20" />
        </div>
      </div>

      {/* Desktop Executive Header */}
      <div className="hidden sm:flex bg-white p-3.5 rounded-xl border border-[#e2e0db] shadow-2xs items-center justify-between gap-2.5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Shimmer className="h-5 w-60" />
            <Shimmer className="h-5 w-28 rounded-full" />
          </div>
          <Shimmer className="h-3 w-72" />
        </div>
        <div className="flex items-center gap-2">
          <Shimmer className="h-8 w-36 rounded-lg font-mono" />
          <Shimmer className="w-8 h-8 rounded-lg" />
        </div>
      </div>

      {/* 4 KPI Stat Cards */}
      <div className="hidden lg:grid lg:grid-cols-4 gap-3.5">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-[#e2e0db] p-3.5 shadow-2xs space-y-1.5">
            <div className="flex items-center justify-between">
              <Shimmer className="h-2.5 w-24" />
              <Shimmer className="w-7 h-7 rounded-lg" />
            </div>
            <Shimmer className="h-6 w-28 font-mono" />
            <Shimmer className="h-2 w-16" />
          </div>
        ))}
      </div>

      {/* Filter Row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1.5">
          <Shimmer className="h-8 w-20 rounded-lg" />
          <Shimmer className="h-8 w-24 rounded-lg" />
          <Shimmer className="h-8 w-24 rounded-lg" />
        </div>
        <div className="flex gap-2">
          <Shimmer className="h-8 w-24 rounded-lg" />
          <Shimmer className="h-8 w-20 rounded-lg" />
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-xl border border-[#e2e0db] shadow-2xs overflow-hidden">
        <div className="p-3 bg-[#faf9f6] border-b border-[#e2e0db] flex justify-between items-center">
          <Shimmer className="h-3.5 w-40" />
          <Shimmer className="h-3.5 w-20" />
        </div>
        <div className="divide-y divide-[#e2e0db]">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="p-3 flex items-center justify-between">
              <div className="space-y-1">
                <Shimmer className="h-3.5 w-24 font-mono" />
                <Shimmer className="h-2.5 w-44" />
              </div>
              <div className="flex items-center gap-3">
                <Shimmer className="h-4 w-16 font-mono" />
                <Shimmer className="h-4 w-16 font-mono" />
                <Shimmer className="h-4 w-20 font-mono font-bold" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 19. RETAILER PRICE LIST SKELETON (1:1 with PriceList.js)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailerPriceListSkeleton() {
  return (
    <div className="space-y-4 pb-20 sm:pb-12 w-full">
      {/* Header Banner */}
      <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 sm:p-5 shadow-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Shimmer className="h-6 w-48" />
            <Shimmer className="h-5 w-28 rounded-full" />
          </div>
          <Shimmer className="h-3 w-56" />
        </div>
        <Shimmer className="h-9 w-36 rounded-xl" />
      </div>

      {/* Search & Category Chips */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Shimmer className="h-9 flex-1 rounded-xl" />
        <div className="flex gap-1.5 overflow-hidden">
          {[1, 2, 3, 4, 5].map((i) => (
            <Shimmer key={i} className="h-9 w-20 rounded-xl shrink-0" />
          ))}
        </div>
      </div>

      {/* Category Section Group */}
      <div className="space-y-3">
        <div className="flex items-center justify-between pb-1 border-b border-[#f0eee9]">
          <Shimmer className="h-4 w-32" />
          <Shimmer className="h-3 w-16" />
        </div>
        <div className="bg-white rounded-2xl border border-[#e2e0db] shadow-xs overflow-hidden">
          <div className="divide-y divide-[#f0eee9]">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="p-3 sm:p-3.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Shimmer className="w-8 h-8 rounded-lg shrink-0" />
                  <div className="space-y-1 flex-1">
                    <Shimmer className="h-3.5 w-36 sm:w-48" />
                    <Shimmer className="h-2.5 w-20" />
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Shimmer className="h-5 w-20 rounded font-mono" />
                  <Shimmer className="h-8 w-20 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 20. RETAILER PROFILE SKELETON (1:1 with Profile.js)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailerProfileSkeleton() {
  return (
    <div className="w-full max-w-4xl mx-auto space-y-4 pb-28 lg:pb-12">
      {/* Store Header Card */}
      <div className="bg-[#1e3a5f] rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <Shimmer className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/25 shrink-0" />
          <div className="space-y-1.5 min-w-0">
            <div className="flex items-center gap-2">
              <Shimmer className="h-6 w-36 sm:w-48 bg-white/30" />
              <Shimmer className="h-4 w-16 rounded-full bg-emerald-400/30" />
            </div>
            <Shimmer className="h-3 w-40 bg-white/20" />
            <Shimmer className="h-3 w-28 bg-white/20" />
          </div>
        </div>
        <Shimmer className="h-9 w-32 rounded-xl bg-white/20 shrink-0" />
      </div>

      {/* 3 KPI Stats Grid */}
      <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white p-3 sm:p-4 rounded-2xl border border-[#e2e0db] shadow-xs space-y-1 text-center">
            <Shimmer className="h-2.5 w-16 mx-auto" />
            <Shimmer className="h-6 w-20 mx-auto font-mono" />
          </div>
        ))}
      </div>

      {/* Action Quick-Tiles (4 Grid) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white p-3.5 rounded-2xl border border-[#e2e0db] shadow-xs flex flex-col items-center gap-2 text-center">
            <Shimmer className="w-9 h-9 rounded-xl bg-[#1e3a5f]/15" />
            <Shimmer className="h-3 w-20" />
          </div>
        ))}
      </div>

      {/* Account Details Metadata Card */}
      <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 sm:p-5 shadow-xs space-y-3">
        <Shimmer className="h-4 w-32" />
        <div className="divide-y divide-[#f0eee9]">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="py-2.5 flex justify-between items-center">
              <Shimmer className="h-3 w-24" />
              <Shimmer className="h-3.5 w-36" />
            </div>
          ))}
        </div>
      </div>

      {/* Logout Button */}
      <div className="pt-1">
        <Shimmer className="h-11 w-full rounded-xl bg-red-500/20" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 21. RETAILER SUPPORT SKELETON (1:1 with Support.js)
// ─────────────────────────────────────────────────────────────────────────────
export function RetailerSupportSkeleton() {
  return (
    <div className="w-full space-y-4 pb-28 lg:pb-12 max-w-4xl mx-auto">
      {/* Helpdesk Header Banner with Direct Hotline & WhatsApp Pills */}
      <div className="bg-[#1e3a5f] rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="space-y-1">
          <Shimmer className="h-5 w-44 bg-white/30" />
          <Shimmer className="h-3 w-56 bg-white/20" />
        </div>
        <div className="flex gap-2">
          <Shimmer className="h-9 w-28 rounded-xl bg-white/20" />
          <Shimmer className="h-9 w-32 rounded-xl bg-emerald-500/40" />
        </div>
      </div>

      {/* 4 KPI Counters Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-[#e2e0db] p-3 shadow-xs space-y-1">
            <Shimmer className="h-2.5 w-20" />
            <Shimmer className="h-6 w-14 font-mono" />
          </div>
        ))}
      </div>

      {/* New Ticket Composer Card */}
      <div className="bg-white rounded-2xl border border-[#e2e0db] p-4 sm:p-5 shadow-xs space-y-3">
        <Shimmer className="h-4 w-36" />
        <div className="space-y-2">
          <Shimmer className="h-9 w-full rounded-xl" />
          <Shimmer className="h-20 w-full rounded-xl" />
        </div>
        <Shimmer className="h-10 w-36 rounded-xl bg-[#1e3a5f]/30 ml-auto" />
      </div>

      {/* Category Chips & Ticket History Cards */}
      <div className="space-y-3">
        <div className="flex gap-1.5 overflow-hidden">
          {[1, 2, 3, 4].map((i) => (
            <Shimmer key={i} className="h-7 w-20 rounded-lg shrink-0" />
          ))}
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-2xl border border-[#e2e0db] p-4 shadow-xs space-y-2">
            <div className="flex justify-between items-center">
              <Shimmer className="h-3.5 w-28" />
              <Shimmer className="h-4 w-16 rounded-full" />
            </div>
            <Shimmer className="h-3 w-full" />
            <Shimmer className="h-2 w-20" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 22. RETAILER BLOCKED SKELETON (1:1 with Blocked.js)
// ─────────────────────────────────────────────────────────────────────────────
export function BlockedSkeleton() {
  return (
    <div className="w-full max-w-5xl mx-auto pt-3 sm:pt-6 pb-28 sm:pb-32 lg:pb-12 px-3 sm:px-6 space-y-4">
      {/* Alert Header Card */}
      <div className="p-4 sm:p-6 rounded-2xl bg-red-50 border border-red-200 shadow-xs space-y-2">
        <div className="flex items-center gap-3">
          <Shimmer className="w-10 h-10 rounded-xl bg-red-200 shrink-0" />
          <div className="space-y-1">
            <Shimmer className="h-5 w-48 bg-red-200" />
            <Shimmer className="h-3 w-36 bg-red-100" />
          </div>
        </div>
      </div>

      {/* Dues & Instructions Card */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 bg-white rounded-2xl border border-[#e2e0db] p-5 shadow-xs space-y-3">
          <Shimmer className="h-4 w-40" />
          <div className="p-4 rounded-xl bg-[#faf9f6] border border-[#e2e0db] space-y-1">
            <Shimmer className="h-3 w-28" />
            <Shimmer className="h-8 w-40 font-mono" />
          </div>
          <Shimmer className="h-3 w-full" />
          <Shimmer className="h-3 w-3/4" />
        </div>

        <div className="lg:col-span-4 bg-white rounded-2xl border border-[#e2e0db] p-5 shadow-xs space-y-3">
          <Shimmer className="h-4 w-32" />
          <Shimmer className="h-10 w-full rounded-xl bg-emerald-600/30" />
          <Shimmer className="h-10 w-full rounded-xl bg-[#1e3a5f]/30" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 23. INTELLIGENT ROUTE-AWARE SKELETON FALLBACK FOR SUSPENSE
// ─────────────────────────────────────────────────────────────────────────────
export function PageSkeletonFallback({ type = 'retailer' }) {
  const location = useLocation();
  const path = location.pathname;

  if (type === 'admin') {
    if (path === '/admin') return <DashboardSkeleton />;
    if (path === '/admin/retailers') return <RetailersSkeleton />;
    if (path === '/admin/daily-ledger') return <DailyLedgerSkeleton />;
    if (path === '/admin/company-order') return <CompanyOrderSkeleton />;
    if (path === '/admin/inventory') return <InventorySkeleton />;
    if (path === '/admin/ledger') return <LedgerSkeleton />;
    if (path === '/admin/support') return <SupportTicketsSkeleton />;
    if (path === '/admin/place-order' || path === '/admin/place_order') return <AdminPlaceOrderSkeleton />;
    if (path === '/admin/announcements') return <AdminAnnouncementsSkeleton />;
    if (path === '/admin/settings') return <AdminSettingsSkeleton />;
    if (path === '/admin/sessions') return <SessionsSkeleton />;
    if (path.startsWith('/admin/orders/')) return <OrderDetailSkeleton />;
    return <DashboardSkeleton />;
  }

  // Retailer routes (supports both /portal/... and direct /... aliases)
  const normPath = path.replace(/^\/portal/, '') || '/';

  if (normPath === '/' || path === '/portal') return <RetailerHomeSkeleton />;
  if (normPath === '/order') return <RetailerPlaceOrderSkeleton />;
  if (normPath === '/track') return <RetailerTrackOrderSkeleton />;
  if (normPath === '/history') return <RetailerOrderHistorySkeleton />;
  if (normPath === '/prices') return <RetailerPriceListSkeleton />;
  if (normPath === '/my-ledger') return <RetailerMyLedgerSkeleton />;
  if (normPath === '/profile' || normPath === '/settings') return <RetailerProfileSkeleton />;
  if (normPath === '/support' || normPath === '/guide') return <RetailerSupportSkeleton />;
  if (normPath === '/checkout') return <RetailerCheckoutSkeleton />;
  if (normPath === '/blocked') return <BlockedSkeleton />;

  return <RetailerHomeSkeleton />;
}

// ── Legacy Compatibility Wrappers ───────────────────────────────────────────
export function TableSkeleton() {
  return <RetailersSkeleton />;
}
export function CardsSkeleton() {
  return <RetailerHomeSkeleton />;
}
export function ProfileSkeleton() {
  return <RetailerProfileSkeleton />;
}
export function OrderSkeleton() {
  return <RetailerPlaceOrderSkeleton />;
}

export default function LoadingSkeleton() {
  return <RetailerHomeSkeleton />;
}
